import { pipe, find, propEq, all, filter, prop, none } from 'ramda';
import { createSelector } from 'reselect';

import { REPORT_TYPES } from './constants';
import { extractFilterIds } from '../selectors';
import { getEndDateForAPI, getStartDateForAPI } from '../filters/selectors';
import { isBrandViewActive, getAllBarbers, getActiveShop, getShops } from '../../../auth/selectors';
import {
  getAll as getActiveShopBarbers,
} from '../../barbers/selector';

export const getReportDefinitions = state => state.reports.customReports.reportDefinitions.items;
export const isReportDefinitionsFetched = state => !!state.reports.customReports.reportDefinitions.fetched;
export const isReportDefinitionsError = state => !!state.reports.customReports.reportDefinitions.error;
export const getReportDefinitionById = (state, id) => pipe(
  getReportDefinitions,
  find(propEq('id', id)),
)(state);

export const getMetricsDefinitions = state => state.reports.customReports.metricsDefinitions.items;
export const isMetricsDefinitionsFetched = state => !!state.reports.customReports.metricsDefinitions.fetched;

export const getReportData = state => state.reports.customReports.reportData;
export const isReportDataFetched = state => state.reports.customReports.isReportDataFetched;

export const isFiltersDirty = state => state.reports.customReports.isFiltersDirty;

export const getBarbersForReport = (state) => {
  if (isBrandViewActive(state)) {
    return getAllBarbers(state);
  }
  return getActiveShopBarbers(state);
};

export const getShopsForReport = (state) => {
  if (isBrandViewActive(state)) {
    return getShops(state);
  }
  return [getActiveShop(state)];
};

export const getBarberFilters = state => state.reports.customReports.barberFilters;
export const getShopFilters = state => state.reports.customReports.shopFilters;

// we have to send all shop ids to custom reports api if 'select all' is checked
// unlike other reports where [] means 'select all'
export const getShopFilterForApi = createSelector(
  getShopFilters,
  extractFilterIds,
);

export const getActiveBarberFilterIds = createSelector(
  getBarberFilters,
  extractFilterIds,
);

// we have to send all barber ids to custom reports api if 'select all' is checked
// unlike other reports where [] means 'select all'
// but only if it's 'barber' report.
export const getBarberFilterForApi = (state, reportDefinition) => {
  if (reportDefinition.type === REPORT_TYPES.BARBER) {
    return getActiveBarberFilterIds(state);
  }
  return [];
};

export const getShopFilterAllChecked = createSelector(
  getShopFilters,
  all(item => item.checked),
);

export const getBarberFilterAllChecked = createSelector(
  getBarberFilters,
  all(item => item.checked),
);

export const getShopFilterAllUnchecked = createSelector(
  getShopFilters,
  none(item => item.checked),
);

export const getBarberFilterAllUnchecked = createSelector(
  getBarberFilters,
  none(item => item.checked),
);

export const isAllFiltersUnchecked = (state, reportDefinition) => (
  reportDefinition.type === REPORT_TYPES.BARBER ?
    getBarberFilterAllUnchecked(state) :
    getShopFilterAllUnchecked(state)
);

export const getCustomReportFilters = (state, reportDefinition) => ({
  fromDate: getStartDateForAPI(state),
  toDate: getEndDateForAPI(state),
  barberFilter: getBarberFilterForApi(state, reportDefinition),
  shopFilter: getShopFilterForApi(state),
});

export const getCRFilteredItems = (state, reportDefinition) => {
  const getItems = reportDefinition.type === REPORT_TYPES.BARBER ? getBarberFilters : getShopFilters;
  return pipe(
    getItems,
    filter(prop('checked'))
  )(state);
};
